import { useCallback, useEffect, useMemo } from 'react';
import {
  Colors,
  Flex,
  IconArrowRight,
  Typography,
} from '@banhala/ui-kit';
import { HorizontalDragScrollView } from '../HorizontalDragScrollView';
import type {
  DownloadedCouponsProps,
  DownloadedCouponEntity,
} from './types';
import { isNotNil } from '../../../../utils';
import { useOpenDeeplink } from '../../../../hooks';
import { useLogAnalyticsEvent } from '../../../../hooks/useLogAnalyticsEvent';
import type { IScreensLogging } from '../../../../api/types/IScreenLogging';

export const DownloadedCoupons = ({
  entity,
  body,
  logging,
}: DownloadedCouponsProps) => {
  const { openDeeplink } = useOpenDeeplink();
  const logAnalyticsEvent = useLogAnalyticsEvent();

  const handlePressViewAll = useCallback(() => {
    if (!body?.item.more_deeplink) return;
    logAnalyticsEvent({
      name: 'CLICK_MORE',
      analytics: { ...logging?.analytics, POSITION: '더보기' },
      ably_only_log: { ...logging?.ably_only_log },
    });
    openDeeplink(body.item.more_deeplink);
  }, [body, logging, openDeeplink, logAnalyticsEvent]);

  if (!entity || entity.length === 0) return null;

  const isSingle = entity.length === 1;

  return (
    <Flex padding={{ top: 8, horizontal: 16, bottom: 16 }}>
      <Flex
        backgroundColor="bg_secondary"
        borderRadius={8}
        padding={{ bottom: 16 }}
      >
        {isNotNil(body) && (
          <Flex.Row
            alignItems="center"
            justifyContent="space-between"
            padding={{ horizontal: 16, vertical: 12 }}
          >
            <Typography type="subtitle1" color="content_primary">
              {body.item.title}
            </Typography>
            {body.item.has_more && (
              <Flex onPress={handlePressViewAll}>
                <Typography type="body3" color="content_secondary">
                  전체보기
                </Typography>
              </Flex>
            )}
          </Flex.Row>
        )}
        {isSingle ? (
          <SingleCoupon
            coupon={entity[0]}
            logging={logging}
            onPressMore={handlePressViewAll}
          />
        ) : (
          <HorizontalDragScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{
              paddingHorizontal: 16,
              gap: 4,
            }}
          >
            {entity.map((coupon, index) => (
              <MultiCouponCard
                key={coupon.item.sno}
                coupon={coupon}
                logging={logging}
                index={index}
              />
            ))}
            {body?.item.has_more && (
              <Flex
                width={120}
                height={128}
                alignItems="center"
                justifyContent="center"
                onPress={handlePressViewAll}
              >
                <Flex
                  width={40}
                  height={40}
                  borderRadius={20}
                  borderWidth={1}
                  borderColor="border_tertiary"
                  alignItems="center"
                  justifyContent="center"
                >
                  <IconArrowRight size={20} fill={Colors.content_secondary} />
                </Flex>
              </Flex>
            )}
          </HorizontalDragScrollView>
        )}
      </Flex>
    </Flex>
  );
};

const SingleCoupon = ({
  coupon,
  logging,
  onPressMore,
}: {
  coupon: DownloadedCouponEntity;
  logging: IScreensLogging;
  onPressMore: () => void;
}) => {
  const { item } = coupon;
  const { openDeeplink } = useOpenDeeplink();
  const logAnalyticsEvent = useLogAnalyticsEvent();
  const expiredText = useCouponExpiredText(item.finished_at);

  useEffect(() => {
    logAnalyticsEvent({
      name: 'DISPLAY_COUPON',
      analytics: { ...logging?.analytics, ...coupon.logging?.analytics, INDEX: 0 },
      ably_only_log: { ...logging?.ably_only_log, ...coupon.logging?.ably_only_log },
    });
  }, [logging, coupon.logging, logAnalyticsEvent]);

  const handlePress = useCallback(() => {
    logAnalyticsEvent({
      name: 'CLICK_COUPON',
      analytics: { ...logging?.analytics, ...coupon.logging?.analytics, INDEX: 0 },
      ably_only_log: { ...logging?.ably_only_log, ...coupon.logging?.ably_only_log },
    });
    openDeeplink(item.deeplink);
  }, [item.deeplink, logging, coupon.logging, openDeeplink, logAnalyticsEvent]);

  return (
    <Flex.Row
      margin={{ horizontal: 16 }}
      backgroundColor="white"
      borderRadius={8}
      height={90}
      onPress={handlePress}
      alignItems="center"
    >
      <Flex flex={1} padding={{ horizontal: 16 }} gap={2}>
        <Typography type="h3" color="content_primary">
          {item.discount_text}
        </Typography>
        <Typography type="body4" color="content_secondary" numberOfLines={1}>
          {item.name}
        </Typography>
        {isNotNil(expiredText) && (
          <Flex margin={{ top: 4 }}>
            <Typography type="body5" color="content_pink_primary">
              {expiredText}
            </Typography>
          </Flex>
        )}
      </Flex>
      <Flex
        margin={{ right: 16 }}
        width={40}
        height={40}
        borderRadius={20}
        borderWidth={1}
        borderColor="border_tertiary"
        alignItems="center"
        justifyContent="center"
        onPress={onPressMore}
      >
        <IconArrowRight size={24} fill={Colors.content_secondary} />
      </Flex>
    </Flex.Row>
  );
};

const MultiCouponCard = ({
  coupon,
  logging,
  index,
}: {
  coupon: DownloadedCouponEntity;
  logging: IScreensLogging;
  index: number;
}) => {
  const { item } = coupon;
  const { openDeeplink } = useOpenDeeplink();
  const logAnalyticsEvent = useLogAnalyticsEvent();
  const expiredText = useCouponExpiredText(item.finished_at);

  useEffect(() => {
    logAnalyticsEvent({
      name: 'DISPLAY_COUPON',
      analytics: { ...logging?.analytics, ...coupon.logging?.analytics, INDEX: index },
      ably_only_log: { ...logging?.ably_only_log, ...coupon.logging?.ably_only_log },
    });
  }, [logging, coupon.logging, index, logAnalyticsEvent]);

  const handlePress = useCallback(() => {
    logAnalyticsEvent({
      name: 'CLICK_COUPON',
      analytics: { ...logging?.analytics, ...coupon.logging?.analytics, INDEX: index },
      ably_only_log: { ...logging?.ably_only_log, ...coupon.logging?.ably_only_log },
    });
    openDeeplink(item.deeplink);
  }, [item.deeplink, logging, coupon.logging, index, openDeeplink, logAnalyticsEvent]);

  return (
    <Flex
      width={120}
      height={128}
      backgroundColor="white"
      borderRadius={8}
      onPress={handlePress}
      padding={{ horizontal: 12, vertical: 12 }}
      justifyContent="space-between"
    >
      <Flex gap={4}>
        <Typography type="h3" color="content_primary" numberOfLines={1}>
          {item.discount_text}
        </Typography>
        <Typography type="body4" color="content_secondary" numberOfLines={2}>
          {item.name}
        </Typography>
      </Flex>
      <Flex.Row alignItems="flex-end" justifyContent="space-between">
        {isNotNil(expiredText) ? (
          <Typography type="body5" color="content_pink_primary" numberOfLines={1}>
            {expiredText}
          </Typography>
        ) : (
          <Flex />
        )}
        <IconArrowRight size={20} fill={Colors.content_secondary} />
      </Flex.Row>
    </Flex>
  );
};

const useCouponExpiredText = (finishedAt?: string): string | null => {
  return useMemo(() => {
    if (!finishedAt) return null;
    const now = new Date();
    const expiry = new Date(finishedAt);
    const diffMs = expiry.getTime() - now.getTime();
    if (diffMs <= 0) return null;
    const diffHours = diffMs / (1000 * 60 * 60);
    if (diffHours > 24) return null;
    const hours = Math.floor(diffHours);
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return `${hours}시간 남음`;
    return `${minutes}분 남음`;
  }, [finishedAt]);
};
